import { useQuery, QueryStatus } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { TrendingUp } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { getScoreFillColor } from "@/lib/chart";
import { formatDate, message } from "@/lib/utils";
import { BrandGovernanceApiFactory, type CreativesScorecardParams } from "@/apis/brand-os/brand-governance";
import type { CreativesScorecardData } from "@/schema/brand-os/brand-governance";

const chartConfig: ChartConfig = {
  score: { label: "Compliance score", color: "var(--muted-foreground)" },
};

function ScoreTrendContent({ data }: { data: CreativesScorecardData }) {
  const trendData = data.score_trend?.daily ?? [];

  return (
    <ChartContainer config={chartConfig} className="w-full h-48">
      <BarChart accessibilityLayer data={trendData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
          tickFormatter={(val) => formatDate(val, "MMM d")}
          interval="preserveStartEnd"
        />
        <YAxis domain={[0, 100]} tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} tickFormatter={(val) => `${val}%`} />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              className="font-semibold"
              labelFormatter={(val) => <p className="text-muted-foreground">{formatDate(val, "MMM d, yyyy")}</p>}
              formatter={(value) => (
                <span className="text-muted-foreground">
                  Score: <span className="font-medium text-foreground">{value}%</span>
                </span>
              )}
            />
          }
        />
        <Bar dataKey="score" radius={[3, 3, 0, 0]} barSize={24}>
          {trendData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getScoreFillColor(entry.score ?? 0)} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

function ScoreTrendComponent({ status, data }: { status: QueryStatus; data?: CreativesScorecardData }) {
  switch (status) {
    case "pending":
      return <ScoreTrendSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return data ? <ScoreTrendContent data={data} /> : <LockedContent message={message} />;
  }
}

export function ScoreTrend({ params }: { params: CreativesScorecardParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.CreativesScorecard(params));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 pb-2">
        <div className="flex items-center gap-2">
          <div className="flex size-9 items-center justify-center rounded-lg bg-blue-100">
            <TrendingUp className="size-5 text-blue-600" />
          </div>
          <div className="flex flex-col gap-0.5">
            <CardTitle>Score Trend</CardTitle>
            <p className="text-xs text-muted-foreground">(30 Days)</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Daily compliance score</p>
      </CardHeader>
      <CardContent className="pt-0">
        <ScoreTrendComponent status={status} data={data?.data} />
      </CardContent>
    </Card>
  );
}

function ScoreTrendSkeleton() {
  return (
    <div className="w-full h-48 flex items-end gap-1 px-1">
      {[45, 62, 38, 71, 55, 48, 80, 58, 42, 65, 52, 73, 59, 46].map((pct, i) => (
        <Skeleton key={i} className="flex-1 min-w-2 rounded-t-md" style={{ height: `${pct}%` }} />
      ))}
    </div>
  );
}